import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { SupportTicketAttachment } from '../data-models/support-ticket-attachment.model';
import { SupportTicketAttachmentUploadResult } from '../response-models/support-ticket-attachment-upload-result.model';
import { isRecord } from '../utils/is-record';
import { countOrZero } from '../utils/wire-value-utils';

/**
 * A support ticket's attachments: `/support-tickets/:ticketId/attachments` (hub `user-support-tickets-controller.ts`).
 * Callers go through `SupportTicketService`, which keeps the ticket-level API. Rejects with an `ApiError`.
 */
@Injectable({
  providedIn: 'root'
})
export class SupportTicketAttachmentService extends BaseService<SupportTicketAttachment> {
  constructor() {
    super('attachments', SupportTicketAttachment);
  }

  /** The ticket's attachments. */
  listForTicket(ticketId: string): Observable<SupportTicketAttachment[]> {
    return this.getMany({ prefix: ticketPrefix(ticketId) });
  }

  /**
   * Upload files as multipart form data (the hub reads every part named `file`; max 5).
   */
  uploadToTicket(ticketId: string, files: File[]): Observable<SupportTicketAttachmentUploadResult> {
    const formData = new FormData();
    for (const file of files) {
      formData.append('file', file, file.name);
    }
    return this.request('POST', { prefix: ticketPrefix(ticketId) }, data => this.toUploadResult(data), formData);
  }

  /**
   * GET `/support-tickets/:ticketId/attachments/:id/download` as a `Blob`. The hub proxies Zoho Desk with its
   * own auth, so the request must carry the user's token: never link to this route with a plain `href`.
   */
  downloadFromTicket(ticketId: string, attachmentId: string): Observable<Blob> {
    return this.download({ prefix: ticketPrefix(ticketId), id: attachmentId, path: 'download' });
  }

  private toUploadResult(data: unknown): SupportTicketAttachmentUploadResult {
    const source = isRecord(data) ? data : {};
    const items = source['attachments'];
    const errors = source['errors'];
    return {
      attachments: Array.isArray(items) ? items.map(item => this.createType(item)) : [],
      uploaded: countOrZero(source['uploaded']),
      errors: Array.isArray(errors) ? errors.filter((e): e is string => typeof e === 'string') : undefined,
    };
  }
}

function ticketPrefix(ticketId: string): string[] {
  return ['support-tickets', ticketId];
}
